import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { bridgeApi } from '@/lib/bridgeApi';

interface HealthCampOrganizer {
  id: string;
  organizer_name: string;
  organizer_type: string;
  email: string;
  contact_number: string;
  alt_contact: string;
  whatsapp?: string;
  contact_person: string;
  city: string;
  state: string;
  address: string;
  city_id?: string;
  state_id?: string;
  area_id?: string;
  area?: string;
  pincode?: string;
  registration_number: string;
  website: string;
  logo_url: string;
  description: string;
  kyc_status: string;
  is_verified: boolean;
  is_active: boolean;
  subscription_plan: string;
  latitude?: string | null;
  longitude?: string | null;
  adminSession?: boolean;
  // AI profile-onboarding — is_paid_plan gates which extracted fields the
  // Profile page can auto-fill; ai_extracted_profile_json (raw JSON string)
  // may hold paid-tier field values from a past extraction done while on a
  // free plan, revealed client-side (no new AI call) after an upgrade.
  is_paid_plan?: boolean;
  ai_extracted_profile_json?: string | null;
}

interface HealthCampAuthContextType {
  organizer: HealthCampOrganizer | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAdminSession: boolean;
  organizerToken: string | null;
  login: (email: string, password: string) => Promise<{ success: boolean; message: string }>;
  register: (data: any) => Promise<{ success: boolean; message: string }>;
  logout: () => void;
  refreshOrganizer: () => Promise<void>;
}

const HealthCampAuthContext = createContext<HealthCampAuthContextType | undefined>(undefined);

const STORAGE_KEY = 'healthcamp_auth';
const TOKEN_KEY = 'healthcamp_auth_token';

export const HealthCampAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [organizer, setOrganizer] = useState<HealthCampOrganizer | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdminSession, setIsAdminSession] = useState(false);
  const [organizerToken, setOrganizerToken] = useState<string | null>(null);

  // Check if this is an admin impersonation session
  const checkAdminImpersonation = (organizerId: string): boolean => {
    try {
      const raw = localStorage.getItem('patakare_impersonation');
      if (!raw) return false;
      const parsed = JSON.parse(raw);
      return (
        parsed?.adminSession === true &&
        parsed?.entityType === 'healthcamp_organizer' &&
        String(parsed?.entityId) === String(organizerId)
      );
    } catch {
      return false;
    }
  };

  // Deep sanitize: convert any non-primitive field to a safe string
  const deepSanitizeOrganizer = (raw: any): HealthCampOrganizer => {
    const s = (val: any, fallback = ''): string => {
      if (val === null || val === undefined) return fallback;
      if (typeof val === 'string') return val;
      if (typeof val === 'number') return String(val);
      if (typeof val === 'object') {
        // Try to extract a useful value from common patterns
        if (val.value) return String(val.value);
        if (val.name) return String(val.name);
        if (val.title) return String(val.title);
        console.warn('[HealthCampAuth] Object in field, discarding:', JSON.stringify(val));
        return fallback;
      }
      return String(val);
    };
    return {
      id: s(raw.id),
      // IMPORTANT: Do NOT fall back to raw.name — that column stores person names (camp creators),
      // not organization names. Only use organizer_name or organization_name.
      organizer_name: s(raw.organizer_name) || s(raw.organization_name) || 'Unknown Organizer',
      organizer_type: s(raw.organizer_type),
      email: s(raw.email),
      contact_number: s(raw.contact_number) || s(raw.phone) || s(raw.mobile),
      alt_contact: s(raw.alt_contact),
      whatsapp: s(raw.whatsapp),
      contact_person: s(raw.contact_person) || s(raw.contact_name) || s(raw.name),
      city: s(raw.city),
      state: s(raw.state),
      address: s(raw.address),
      // These 7 were declared on the HealthCampOrganizer type below but never
      // actually assigned here — every refresh (including the one Profile's
      // own handleSave() triggers right after a successful save) silently
      // dropped them, so the State/City/Area/GPS fields went blank on screen
      // even though the backend had genuinely saved the real values.
      city_id: s(raw.city_id) || undefined,
      state_id: s(raw.state_id) || undefined,
      area_id: s(raw.area_id) || undefined,
      area: s(raw.area),
      pincode: s(raw.pincode),
      latitude: raw.latitude != null ? s(raw.latitude) : null,
      longitude: raw.longitude != null ? s(raw.longitude) : null,
      registration_number: s(raw.registration_number),
      website: s(raw.website),
      logo_url: s(raw.logo_url),
      description: s(raw.description),
      kyc_status: s(raw.kyc_status, 'pending'),
      is_verified: !!raw.is_verified,
      is_active: !!raw.is_active,
      subscription_plan: s(raw.subscription_plan, 'free'),
      adminSession: raw.adminSession,
      is_paid_plan: !!raw.is_paid_plan,
      ai_extracted_profile_json: raw.ai_extracted_profile_json != null ? String(raw.ai_extracted_profile_json) : null,
    };
  };

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const sanitized = deepSanitizeOrganizer(parsed);
        const isAdminImpersonation = parsed.adminSession || checkAdminImpersonation(sanitized.id);
        
        // For admin sessions, skip verification but refresh subscription
        if (isAdminImpersonation) {
          sanitized.adminSession = true;
          setIsAdminSession(true);
          // Real provider token IS in localStorage (set when the impersonation
          // session was created) — this branch just never read it into state,
          // so every subsequent healthcamp_organizer_get/update call sent
          // token=null, which made getAuthHeaders() fall back to attaching the
          // ADMIN's own bearer token instead. requireProviderAuth(9) on the
          // backend rejects that (wrong token shape/type_code entirely), which
          // is exactly the "Unauthorized — provider login required" seen live.
          const storedToken = localStorage.getItem(TOKEN_KEY);
          if (storedToken) setOrganizerToken(storedToken);
          bridgeApi.call<any>('healthcamp_subscription', { organizer_id: sanitized.id })
            .then((subData) => {
              if (subData?.status === 'active' && subData?.plan_name) {
                sanitized.subscription_plan = String(subData.plan_name);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(sanitized));
              }
            })
            .catch(() => {})
            .finally(() => {
              setOrganizer(sanitized);
              setIsLoading(false);
            });
          return;
        }
        
        // For regular sessions, verify but with graceful fallback
        verifyOrganizer(sanitized.id).then((result) => {
          if (result.shouldAccept) {
            setOrganizer(sanitized);
            const storedToken = localStorage.getItem(TOKEN_KEY);
            if (storedToken) setOrganizerToken(storedToken);
          } else {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(TOKEN_KEY);
          }
          setIsLoading(false);
        });
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
        setIsLoading(false);
      }
    } else {
      setIsLoading(false);
    }
  }, []);

  // Graceful verification - returns shouldAccept: true if API fails or doesn't exist
  const verifyOrganizer = async (id: string): Promise<{ shouldAccept: boolean; isActive?: boolean }> => {
    try {
      const result = await bridgeApi.call<any>('healthcamp_organizer_verify', { id });
      // API responded successfully
      if (result?.is_active === true) {
        return { shouldAccept: true, isActive: true };
      }
      if (result?.is_active === false) {
        return { shouldAccept: false, isActive: false };
      }
      // Unknown response - accept session
      return { shouldAccept: true };
    } catch (error: any) {
      // API failed or doesn't exist - gracefully accept session
      console.warn('[HealthCampAuth] Verification skipped (API unavailable):', error.message);
      return { shouldAccept: true };
    }
  };

  const login = async (email: string, password: string): Promise<{ success: boolean; message: string }> => {
    setIsLoading(true);
    
    try {
      const result = await bridgeApi.post<any>('health_camp_login', { email, password });
      
      if (!result.success) {
        setIsLoading(false);
        return { success: false, message: result.message || 'Login failed' };
      }

      const org = result.organizer;

      if (org.kyc_status === 'pending') {
        setIsLoading(false);
        return { success: false, message: 'Your verification is pending. Please wait for admin approval.' };
      }

      if (org.kyc_status === 'rejected') {
        setIsLoading(false);
        return { success: false, message: 'Your registration was rejected. Please contact support.' };
      }

      if (!org.is_active) {
        setIsLoading(false);
        return { success: false, message: 'Your account is currently inactive. Please contact support.' };
      }

      const organizerData = deepSanitizeOrganizer(org);

      setOrganizer(organizerData);
      setIsAdminSession(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(organizerData));

      if (result.token) {
        setOrganizerToken(result.token);
        localStorage.setItem(TOKEN_KEY, result.token);
      }

      // Same install-linking MemberAuthContext's login() already does — was
      // missing here entirely, so a Health Camp organizer who installed the
      // PWA then logged in stayed permanently attributed to "Guest" in
      // Install Analytics, even though they're a real, identifiable account.
      const numericId = typeof organizerData.id === 'string' ? parseInt(organizerData.id) : Number(organizerData.id);
      if (numericId > 0) {
        import('@/utils/installTracker').then(({ linkInstallToUser, isAppInstalled }) => {
          if (isAppInstalled()) linkInstallToUser(numericId).catch(() => {});
        });
      }

      setIsLoading(false);

      return { success: true, message: 'Login successful!' };
    } catch (err: any) {
      setIsLoading(false);
      return { success: false, message: err.message || 'An error occurred. Please try again.' };
    }
  };

  const register = async (data: any): Promise<{ success: boolean; message: string }> => {
    try {
      const result = await bridgeApi.post<any>('healthcamp_register', data);
      if (result && result.success === false) {
        return { success: false, message: result.error || result.errors?.join(', ') || 'Registration failed' };
      }
      return { success: true, message: result?.message || 'Registration submitted' };
    } catch (err: any) {
      return { success: false, message: err.message || 'Registration failed' };
    }
  };

  const logout = () => {
    setOrganizer(null);
    setIsAdminSession(false);
    setOrganizerToken(null);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('patakare_impersonation');
  };

  const refreshOrganizer = async () => {
    if (!organizer) return;

    try {
      const rawData = await bridgeApi.call<any>('healthcamp_organizer_get', { id: organizer.id, token: organizerToken });

      // The API may return { data: {...} } or the organizer object directly
      const orgData = rawData?.data || rawData;
      
      if (orgData && typeof orgData === 'object' && (orgData.id || orgData.organizer_name || orgData.email)) {
        const updated = deepSanitizeOrganizer(orgData);
        updated.adminSession = organizer.adminSession;

        try {
          const subData = await bridgeApi.call<any>('healthcamp_subscription', { organizer_id: organizer.id });
          if (subData?.status === 'active' && subData?.plan_name) {
            updated.subscription_plan = String(subData.plan_name || updated.subscription_plan || 'free');
          }
        } catch {}
        
        // CRITICAL: If the current organizer has a valid name and the refreshed one
        // is different (likely overwritten by person/creator name from backend), keep the original.
        // This handles both admin sessions AND regular sessions where the backend
        // hasn't been updated yet and returns 'name' field in organizer_name.
        const currentName = organizer.organizer_name;
        const refreshedName = updated.organizer_name;
        if (currentName && currentName !== 'Unknown Organizer' && refreshedName !== currentName) {
          console.warn('[HealthCampAuth] Preserving original org name:', currentName, '(API returned:', refreshedName, ')');
          updated.organizer_name = currentName;
        }
        
        setOrganizer(updated);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } else {
        console.warn('[HealthCampAuth] refreshOrganizer: unexpected response format', rawData);
      }
    } catch (e) {
      console.error('Failed to refresh organizer:', e);
    }
  };

  return (
    <HealthCampAuthContext.Provider
      value={{
        organizer,
        isLoading,
        isAuthenticated: !!organizer,
        isAdminSession,
        organizerToken,
        login,
        register,
        logout,
        refreshOrganizer,
      }}
    >
      {children}
    </HealthCampAuthContext.Provider>
  );
};

export const useHealthCampAuth = () => {
  const context = useContext(HealthCampAuthContext);
  if (context === undefined) {
    throw new Error('useHealthCampAuth must be used within a HealthCampAuthProvider');
  }
  return context;
};
